import React, { useEffect, useMemo } from 'react';
import { Container } from '@fiverr-private/layout_components';
import { Heading, Text, Typography } from '@fiverr-private/typography';
import {
    Form,
    FormControl,
    FormElement,
    FormHeader,
    FormHelperText,
    FormLabel,
    Input,
    useForm,
    zodResolver,
} from '@fiverr-private/forms';
import { Button } from '@fiverr-private/button';
import { resetPassword } from '../../../../services/resetPassword';
import { translate } from '../../../../utils/translate';
import { raiseErrorToast } from '../../../../utils/raiseErrorToast';
import { globalMixpanelReporter, MixpanelEventsContext } from '../../../../events/mixpanel';
import { globalBigQueryReporter, BigQueryEventsContext } from '../../../../events/bigQuery';
import { globalGrafanaReporter } from '../../../../events/grafana';
import { useOnceBeforeRenderOnClient } from '../../../../hooks/useOnceBeforeRenderOnClient';
import { FormSchema, createSchema } from './ResetPasswordStepForm.constants';

interface ResetPasswordStepFormProps {
    onSubmit?: ({ redirectUrl }: { redirectUrl?: string }) => void;
    email?: string;
    mixpanelEnrichment?: MixpanelEventsContext;
    bigQueryEnrichment?: BigQueryEventsContext;
}

export const ResetPasswordStepForm = ({
    onSubmit,
    mixpanelEnrichment = {},
    bigQueryEnrichment = {},
    email = '',
}: ResetPasswordStepFormProps) => {
    const resolver = useMemo(() => zodResolver(createSchema()), []);

    const methods = useForm({
        resolver,
        defaultValues: { email },
    });

    const { isSubmitting, isValid } = methods.formState;

    const handleSubmit = async ({ email }: FormSchema) => {
        globalMixpanelReporter.send('Identification - Reset Password - Submit');
        globalBigQueryReporter.send('identification.reset_password.submit');
        globalGrafanaReporter.count('identification.%{platform}.reset_password.submit');

        const { status, redirectUrl, message } = await resetPassword({
            email,
            funnel: 'standard',
        });

        if (status === 'success') {
            onSubmit?.({ redirectUrl });

            globalMixpanelReporter.send('Identification - Reset Password - Completed');
            globalBigQueryReporter.send('identification.reset_password.completed');
            globalGrafanaReporter.count('identification.%{platform}.reset_password.completed');

            return;
        }

        globalMixpanelReporter.send('Identification - Reset Password - Failed');
        globalBigQueryReporter.send('identification.reset_password.failed');
        globalGrafanaReporter.count('identification.%{platform}.reset_password.failed');

        if (message) {
            methods.setError('email', {
                message: translate(`validation.email.${message}`),
            });
        } else {
            raiseErrorToast();
        }
    };

    useOnceBeforeRenderOnClient(() => {
        globalMixpanelReporter.enrichContext(mixpanelEnrichment);
        globalBigQueryReporter.enrichContext(bigQueryEnrichment);
    });

    useEffect(() => {
        globalMixpanelReporter.send('Identification - Reset Password - Show');
        globalBigQueryReporter.send('identification.reset_password.show');
        globalGrafanaReporter.count('identification.%{platform}.reset_password.show');
    }, []);

    return (
        <Container display="flex" direction="column" flexGrow="1">
            <Heading as="h4">{translate('steps.reset_password.title')}</Heading>
            <Text marginTop="2" marginBottom="8" color="bodySecondary">
                {translate('steps.reset_password.subtitle')}
            </Text>
            <Form onSubmit={handleSubmit} methods={methods} flex="1">
                <FormControl name="email">
                    <FormHeader>
                        <FormLabel>
                            <Typography fontWeight="semibold" color="grey_1100">
                                {translate('fields.reset_password_email.label')}
                            </Typography>
                        </FormLabel>
                    </FormHeader>
                    <FormElement>
                        <Input
                            type="email"
                            placeholder={translate('fields.reset_password_email.placeholder')}
                            autoComplete="email"
                            autoFocus
                        />
                    </FormElement>
                    <FormHelperText />
                </FormControl>
                <Container
                    marginTop={{ default: 'auto', md: '0' }}
                    display="flex"
                    direction="column"
                    alignItems="stretch"
                >
                    <Button loading={isSubmitting} disabled={!isValid} type="submit">
                        {translate('steps.reset_password.submit')}
                    </Button>
                </Container>
            </Form>
        </Container>
    );
};
